import { useState, useEffect } from 'react';
import { database, useAuth } from '@/contexts/AuthContext';
import { ref, get, set } from 'firebase/database';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Heart, ExternalLink, Search } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const techStackOptions = [
  'React', 'Angular', 'Vue', 'Node.js', 'Python', 'Java', 'C++',
  'JavaScript', 'TypeScript', 'Django', 'Flask', 'Spring Boot',
  'MongoDB', 'PostgreSQL', 'MySQL', 'Firebase', 'AWS', 'Docker', 'Kubernetes'
];

const statusOptions = ['Idea Stage', 'In Development', 'Completed'];

export default function FeedPage() {
  const { userProfile } = useAuth();
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [techFilter, setTechFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('');
  const [collegeFilter, setCollegeFilter] = useState('');

  useEffect(() => {
    fetchProjects();
  }, [techFilter, statusFilter, collegeFilter]);

  const fetchProjects = async () => {
    try {
      const snapshot = await get(ref(database, `projects`));

      if (!snapshot.exists()) {
        setProjects([]);
        setLoading(false);
        return;
      }

      let list = Object.keys(snapshot.val()).map((id) => ({
        id,
        ...snapshot.val()[id],
      }));

      // 🟦 FIXED TECH STACK FILTER (supports both Array & String)
      if (techFilter && techFilter !== "all") {
        list = list.filter((p) => {
          if (!p.tech_stack) return false;

          if (Array.isArray(p.tech_stack)) {
            return p.tech_stack.includes(techFilter);
          }

          return p.tech_stack === techFilter;
        });
      }

      if (statusFilter && statusFilter !== "all") {
        list = list.filter((p) => p.status === statusFilter);
      }

      if (collegeFilter) {
        list = list.filter((p) =>
          p.college_name?.toLowerCase().includes(collegeFilter.toLowerCase())
        );
      }

      setProjects(list);
    } catch (error) {
      console.log(error);
      toast.error("Failed to load projects");
    }

    setLoading(false);
  };

  const handleLike = async (projectId) => {
    const likeRef = ref(database, `projects/${projectId}/likes/${userProfile.uid}`);

    try {
      const snapshot = await get(likeRef);

      if (snapshot.exists()) {
        await set(likeRef, null);
      } else {
        await set(likeRef, true);
      }

      fetchProjects();
    } catch (error) {
      toast.error("Failed to update like");
    }
  };

  const filteredProjects = projects.filter(
    (p) =>
      p.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-cyan-50 to-blue-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-8">

        {/* SEARCH + FILTER */}
        <div className="glass p-6 rounded-2xl mb-8 space-y-4">
          <div className="flex flex-col md:flex-row gap-4">

            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
              <Input
                placeholder="Search projects..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            
          </div>

          {/* FILTER ROW */}
          <div className="flex flex-col md:flex-row gap-4">

            <Select value={techFilter} onValueChange={setTechFilter}>
              <SelectTrigger className="md:w-[200px]">
                <SelectValue placeholder="Tech Stack" />
              </SelectTrigger>
              <SelectContent className="max-h-64 overflow-y-auto">
  <SelectItem value="all">All Technologies</SelectItem>
  {techStackOptions.map((tech) => (
    <SelectItem key={tech} value={tech}>
      {tech}
    </SelectItem>
  ))}
</SelectContent>

            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="md:w-[200px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {statusOptions.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Input
              placeholder="Filter by college…"
              className="md:w-[200px]"
              value={collegeFilter}
              onChange={(e) => setCollegeFilter(e.target.value)}
            />
          </div>
          <Button
              onClick={() => navigate("/upload")}
              className="bg-gradient-to-r from-sky-500 to-cyan-500 text-white"
            >
              + Upload Project
            </Button>
        </div>

        {/* GRID */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">

          {filteredProjects.map((project) => (
            <Card
              key={project.id}
              className="glass border-white/50 overflow-hidden hover:shadow-md transition-all cursor-pointer"
              
            >
              <CardHeader>
                <div className="flex justify-between items-center mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-400 to-cyan-500 text-white flex items-center justify-center font-bold">
                      {project.user_name?.charAt(0) || "U"}
                    </div>
                    <div>
                      <p className="font-semibold text-sm" onClick={()=>navigate(`/profile/${project.uid}`)}>{project.user_name}</p>
                      <p className="text-xs text-gray-500">{project.college_name}</p>
                    </div>
                  </div>

                  <Badge
                    className={
                      project.status === "Completed"
                        ? "bg-green-100 text-green-800"
                        : project.status === "In Development"
                        ? "bg-blue-100 text-blue-800"
                        : "bg-yellow-100 text-yellow-800"
                    }
                  >
                    {project.status}
                  </Badge>
                </div>

                <CardTitle className="text-lg font-bold break-words line-clamp-2" onClick={() => navigate(`/project/${project.id}`)}>
                  {project.title}
                </CardTitle>

                <CardDescription className="line-clamp-2 break-words">
                  {project.description}
                </CardDescription>
              </CardHeader>

              <CardContent>
                <div className="flex flex-wrap gap-2 mb-4">
                  {(Array.isArray(project.tech_stack)
                    ? project.tech_stack
                    : [project.tech_stack]
                  )
                    .filter(Boolean)
                    .slice(0, 3)
                    .map((tech) => (
                      <Badge key={tech} variant="secondary">
                        {tech}
                      </Badge>
                    ))}

                  {Array.isArray(project.tech_stack) &&
                    project.tech_stack.length > 3 && (
                      <Badge variant="secondary">
                        +{project.tech_stack.length - 3}
                      </Badge>
                    )}
                </div>

                {project.allow_collaboration && (
                  <div className="text-sm text-sky-600 font-medium">
                    ✨ Open for collaboration
                  </div>
                )}
              </CardContent>

              <CardFooter className="flex justify-between border-t pt-4">

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLike(project.id);
                  }}
                  className="gap-2"
                >
                  <Heart
                    className={
                      'w-4 h-4 ' +
                      (project.likes?.[userProfile.uid]
                        ? 'text-red-500 fill-red-500'
                        : '')
                    }
                  />
                  {project.likes ? Object.keys(project.likes).length : 0}
                </Button>

                <Button variant="ghost" size="sm" className="gap-2" onClick={() => navigate(`/project/${project.id}`)}>
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
